import { useState } from 'react'
import { Plus, X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'

type TagsInputProps = {
  value: string[]
  onChange: (tags: string[]) => void
  existingTags: string[]
  isLoading?: boolean
}

export const TagsInput = ({ value, onChange, existingTags, isLoading }: TagsInputProps) => {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')

  const addTag = (tag: string) => {
    const trimmed = tag.trim()
    if (!trimmed || value.includes(trimmed)) return
    onChange([...value, trimmed])
    setSearch('')
  }

  const removeTag = (tag: string) => {
    onChange(value.filter((t) => t !== tag))
  }

  const trimmedSearch = search.trim()
  const suggestions = existingTags.filter(
    (tag) => !value.includes(tag) && tag.toLowerCase().includes(trimmedSearch.toLowerCase()),
  )
  const hasExactMatch = existingTags.some(
    (tag) => tag.toLowerCase() === trimmedSearch.toLowerCase(),
  )

  return (
    <div className="flex flex-col gap-2">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((tag) => (
            <Badge key={tag} variant="neutral" size="sm" className="gap-1 pr-1">
              {tag}
              <button
                type="button"
                onClick={() => removeTag(tag)}
                aria-label={`Remove tag ${tag}`}
                className="rounded-full hover:bg-background/60"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next)
          if (!next) setSearch('')
        }}
      >
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isLoading}
            className="h-9 w-fit justify-start gap-1.5 rounded-lg border border-border bg-background text-sm font-normal text-muted-foreground shadow-sm hover:text-foreground"
          >
            <Plus className="h-3.5 w-3.5" />
            Add tag
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[240px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search or create a tag..."
              value={search}
              onValueChange={setSearch}
              className="h-8 py-1.5 border-0 outline-none ring-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
            />
            <CommandList>
              {trimmedSearch && !hasExactMatch && (
                <CommandItem value={`__create-${trimmedSearch}`} onSelect={() => addTag(search)}>
                  <Plus className="h-4 w-4" />
                  Create tag &quot;{trimmedSearch}&quot;
                </CommandItem>
              )}
              {suggestions.length === 0 && (!trimmedSearch || hasExactMatch) && (
                <CommandEmpty>No tags found.</CommandEmpty>
              )}
              <CommandGroup>
                {suggestions.map((tag) => (
                  <CommandItem key={tag} value={tag} onSelect={() => addTag(tag)}>
                    {tag}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}
